import { UserCustomStatus } from '@modules/user-status/UserCustomStatus';
import { getEffectiveUserStatus } from '@modules/user/utils/getEffectiveUserStatus';
import { Divider, Stack, Typography } from '@mui/material';
import { IWorkspaceUser, UserType } from 'src/@types/workspace-info';
import { useAuthContext } from 'src/auth/useAuthContext';
import { CPAvatar } from 'src/components/base/CPAvatar';
import { useLocales } from 'src/locales';
import { UserProfileShortActions } from './UserProfileShortActions';
import { UserProfileShortSendMessage } from './UserProfileShortSendMessage';
import { useDirectMessageControl } from './useDirectMessageControl';

type UserProfileShortProps = {
  user: IWorkspaceUser;
};

export const UserProfileShort = ({ user }: UserProfileShortProps) => {
  const { translate } = useLocales();
  const { user: currentUser } = useAuthContext();

  const dmControl = useDirectMessageControl(user);

  return (
    <>
      <Stack spacing={1} direction="row" alignItems="center" mb={1}>
        <CPAvatar
          name={user?.name || ''}
          size="medium"
          picture={user?.picture}
          status={getEffectiveUserStatus(user)}
        />
        <Stack direction="column" alignItems="flex-start">
          <Typography variant="body1">{user?.name}</Typography>
          {user.type === UserType.INTEGRATION ? (
            <Typography
              variant="body2"
              color="text.secondary"
              fontStyle="italic"
            >
              Bot
            </Typography>
          ) : user.type === UserType.GUEST ? (
            <Typography
              variant="body2"
              color="text.secondary"
              fontStyle="italic"
            >
              Guest
            </Typography>
          ) : user.title ? (
            <Typography variant="body2" color="text.secondary">
              {user?.title}
            </Typography>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              fontStyle="italic"
            >
              {translate('shared.labels.no_title')}
            </Typography>
          )}
        </Stack>
      </Stack>
      {user.customStatus && <UserCustomStatus sx={{ pl: 5.5 }} user={user} />}

      {user?.type !== UserType.INTEGRATION ? (
        <>
          <Typography variant="overline" color="text.secondary">
            {translate('shared.labels.email')}
          </Typography>
          <Typography
            variant="body2"
            gutterBottom
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {user?.email}
          </Typography>
        </>
      ) : null}

      {(user.type === UserType.USER || user.type === UserType.AI) &&
      user._id !== currentUser?._id ? (
        <Stack
          gap={1}
          mt={2}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <UserProfileShortActions dmControl={dmControl} />
          <Divider sx={{ my: 1.5 }} />
          <UserProfileShortSendMessage dmControl={dmControl} />
        </Stack>
      ) : null}
    </>
  );
};
